import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDownIcon, ChevronUpIcon, PencilSquareIcon } from "@heroicons/react/16/solid";
import { EllipsisVerticalIcon, Square2StackIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Collapse } from "@material-tailwind/react";
import { PropsWithChildren, useState } from "react";

import { Badge, Input, Paragraph } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import { ApiReportTemplateExportFrameworkChoices } from "@api/graphql/types-and-hooks";
import { HoveredAddButtonHint } from "@components/HoveredAddButton/HoveredAddButtonHint";
import { ReportTemplateSection, ReportTemplateSectionField } from "@providers/TemplateEditorProvider";

interface TemplateElementHeaderProps extends PropsWithChildren {
  id: string;
  isCollapsed?: { value: boolean };
  header: string;
  isDefault: boolean;
  isAi?: boolean;
  isSection?: boolean;
  isPlaceHolder?: boolean;
  disableDeleteButton?: boolean;
  disableDeleteButtonReason?: string;
  readonly?: boolean;
  onDelete: (id: string) => void;
  onSectionUpdate?: (updates: Partial<ReportTemplateSection>) => void;
  onFieldUpdate?: (updates: Partial<ReportTemplateSectionField>) => void;
  onDuplicate: (id: string) => void;
  fieldKey: string;
  sectionKey: string;
  isGlobal?: boolean;
  exportFramework?: ApiReportTemplateExportFrameworkChoices;
  fieldType?: string;
  onCollapseChange?: (id: string, isCollapsed: boolean) => void;
}

export const TemplateElementHeader: React.FC<TemplateElementHeaderProps> = ({
  id,
  children,
  isCollapsed = { value: false },
  header,
  isDefault,
  isAi = false,
  isSection = false,
  isPlaceHolder = false,
  disableDeleteButton = false,
  disableDeleteButtonReason = "",
  readonly = false,
  onDelete,
  onSectionUpdate,
  onFieldUpdate,
  onDuplicate,
  isGlobal = false,
  onCollapseChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: id || "",
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const isSectionCollapsed = isCollapsed.value;
  const [isEditing, setIsEditing] = useState(false);
  const [newHeading, setNewHeading] = useState<string>(header);

  const actionButtonMenu: PopoverMenuItem[] = [
    {
      label: `Duplicate ${isSection ? "Section" : "Field"}`,
      Icon: Square2StackIcon,
      callback: () => {
        onDuplicate(id);
      },
    },
    {
      label: `Delete ${isSection ? "Section" : "Field"}`,
      Icon: TrashIcon,
      disabled: disableDeleteButton,
      disableReason: disableDeleteButtonReason,
      callback: () => {
        onDelete(id);
      },
    },
  ];

  const hoverClass = isSection ? "group-hover/section:opacity-100" : "group-hover/field:opacity-100";

  return (
    <section
      ref={setNodeRef}
      style={{ ...style, pageBreakInside: "avoid", breakInside: "avoid" }}
      className={`flex mb-3 flex-col relative duration-0 transition-all outline outline-1 border
        ${!readonly && !isAi && "hover:border-border-focus"}
        ${isAi ? "border-blurple-400 bg-blurple-50" : isSection ? "border-border-strong" : "border-border-strong"}
        outline-white pageBreak rounded-2xl bg-white overflow-visible
        ${isSection ? "add-section-hover group/section" : "add-field-hover group/field"}`}
    >
      {!isPlaceHolder && !readonly && (
        <HoveredAddButtonHint
          className="top-[-26px]"
          hoverClass={isSection ? "add-section-hint" : "add-field-hint"}
        ></HoveredAddButtonHint>
      )}
      <div
        className="template-header-clickable flex items-center justify-between cursor-pointer pl-4 pr-2 pt-3 pb-3"
        onMouseOver={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();

          // Don't expand/collapse when in edit mode
          if (isEditing) {
            return;
          }

          if (onCollapseChange) {
            onCollapseChange(id, !isSectionCollapsed);
          }
        }}
      >
        <div className="flex items-center">
          {!readonly && (
            <div {...attributes} {...listeners} className="text-text-secondary rounded-full cursor-grab mr-2">
              <div className={`flex w-6 ${hoverClass} opacity-0 duration-200 transition-opacity`}>
                <EllipsisVerticalIcon className="h-6 w-6" />
                <EllipsisVerticalIcon className="h-6 w-6 -ml-3" />
              </div>
            </div>
          )}
          <div className="h-6 w-6 p-1 mr-2 rounded-s hover:bg-limestone-50 text-icon-primary">
            <ChevronDownIcon className={`h-4 w-4 ${!isSectionCollapsed && "hidden"}`}></ChevronDownIcon>
            <ChevronUpIcon className={`h-4 w-4 ${isSectionCollapsed && "hidden"}`}></ChevronUpIcon>
          </div>
        </div>
        <div
          data-testid="section-title-row"
          className="flex items-center relative group w-full "
          data-sticky-nav-header
          id={`${id.replace("-", "")}-${header.replace(/\s+/g, "-")}${isPlaceHolder ? "-placeholder" : ""}`}
        >
          {isEditing ? (
            <div className={`relative flex flex-col gap-2 -mt-4`}>
              <Input
                className={`-mb-8 peer font-semibold ${isSection ? "[&_input]:text-base!" : "[&_input]:text-sm!"} block w-full border-0 py-1.5 text-text-primary placeholder-limestone-200 ring-inset ring-limestone-200 placeholder:text-limestone-400 sm:text-sm sm:leading-6 focus:ring-0 focus:outline-hidden cursor-text`}
                type="text"
                defaultValue={newHeading}
                placeholder=""
                onChange={(e) => {
                  setNewHeading(e.currentTarget.value);
                }}
                autoFocus={true}
                onBlur={() => {
                  setIsEditing(false);
                  if (newHeading !== header && !readonly) {
                    if (isSection) {
                      onSectionUpdate?.({ title: newHeading });
                    } else {
                      onFieldUpdate?.({ title: newHeading });
                    }
                  }
                }}
                name={""}
                label={""}
              />
            </div>
          ) : (
            <>
              <Paragraph
                displayType="loud"
                colorType="primary"
                data-dynamic-section
                data-testid="section-title-input-paragraph"
                className={`${!isSection && "text-sm!"} cursor-pointer`}
                onClick={(e) => {
                  if (readonly) return;
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                {newHeading}
              </Paragraph>
              {!readonly && (
                <PencilSquareIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className="h-6 w-6 ml-2 cursor-pointer rounded-2xl bg-limestone-50 hover:bg-limestone-100 p-1 text-text-secondary opacity-0 group-hover:opacity-100"
                />
              )}
            </>
          )}
        </div>
        <div className="flex items-center">
          <div
            className={`flex items-center gap-2 mr-2 ${hoverClass} ${
              isSectionCollapsed && "opacity-0 duration-200 transition-opacity"
            }`}
          >
            {isGlobal && <Badge appearance="neutral" text="Global" className="animate-none!" />}
            {isAi ? (
              <Badge appearance="ai" text="AI" className="animate-none!" />
            ) : (
              <Badge appearance={isDefault ? "neutral" : "warn"} text={isDefault ? "Default" : "Custom"} />
            )}
          </div>

          {!readonly && (
            <div className={`${hoverClass} opacity-0 duration-200 transition-opacity`}>
              <PopoverMenu openDirection="to-right" menuItems={actionButtonMenu} />
            </div>
          )}
        </div>
      </div>
      <Collapse className={`${isSectionCollapsed ? "overflow-hidden" : "overflow-visible"}`} open={!isSectionCollapsed}>
        {isSection && <div className="mb-4 border-t w-full border-limestone-100"></div>}
        <div className={`px-4 ${isSectionCollapsed && "hidden"}`}>{children}</div>
      </Collapse>
      {!isPlaceHolder && !readonly && (
        <HoveredAddButtonHint
          className="-bottom-4"
          hoverClass={isSection ? "add-section-hint" : "add-field-hint"}
        ></HoveredAddButtonHint>
      )}
    </section>
  );
};
